import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createAssignment } from "../pages/services/assignmentService";
import AssignmentQuestionPanel from "./ReusableComponent/AssignmentQuestionPanel";
import RichTextEditor from "./ReusableComponent/RichTextEditor";
import "../styles/AddForm.css";
import "../styles/AddAssignment.css";
import "./ReusableComponent/AssignmentQuestionPanel.css";
import { fetchBatchOptions } from "../pages/services/filterOptionsService";

const DAY_ABBR = { Monday: "M", Tuesday: "T", Wednesday: "W", Thursday: "Th", Friday: "F", Saturday: "Sa", Sunday: "Su" };
const formatBatchLabel = (b) => {
  const days = (b.batchDays || []).map(d => DAY_ABBR[d] || d).join("");
  return `${b.batchTitle} | ${days} - ${b.batchTime}`;
};

const STATUSES = [
  { value: "Open",      label: "Open" },
  { value: "Closed",    label: "Closed" },
  { value: "Completed", label: "Completed" },
];

const DIFFICULTY_CLASS = {
  Easy: "aq-easy", Medium: "aq-medium", Hard: "aq-hard",
  "Very Hard": "aq-very-hard", Professional: "aq-professional",
};

const Field = ({ label, required, error, children }) => (
  <div className="af-field">
    <label className="af-label">{label}{required && <span className="af-required">*</span>}</label>
    {children}
    {error && <span className="af-error">{error}</span>}
  </div>
);

const AddAssignments = () => {
  const [formData, setFormData] = useState({
    assignmentTitle: "", assignmentDuration: "", assignmentDescription: "",
    batchId: "", assignmentStartTime: "", assignmentEndTime: "",
    assignmentAnalysisStartTime: "", assignmentAnalysisEndTime: "",
    assignmentAttachment: "", status: "",
  });
  const [enableAnalysis, setEnableAnalysis] = useState(false);

  const [batches, setBatches] = useState([]);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // Questions state
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [sliderOpen, setSliderOpen] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  useEffect(() => {
    // Shared source: inactive batches are never offered, and the list is not
    // silently truncated by the list endpoint's page size.
    fetchBatchOptions()
      .then(opts => setBatches(opts.map(o => ({ _id: o.value, batchTitle: o.label }))))
      .catch(console.error);
  }, [API_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(p => ({ ...p, [name]: value }));
    if (name === "assignmentStartTime" || name === "assignmentEndTime") {
      setErrors(p => { const n = {...p}; delete n.assignmentTime; return n; });
    } else if (name === "assignmentAnalysisStartTime" || name === "assignmentAnalysisEndTime") {
      setErrors(p => { const n = {...p}; delete n.assignmentAnalysisTime; return n; });
    } else {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const validate = () => {
    const e = {};
    if (!formData.assignmentTitle.trim())       e.assignmentTitle       = "Title is required";
    if (!formData.assignmentDescription?.replace(/<[^>]*>/g, "").trim()) e.assignmentDescription = "Description is required";
    if (!formData.batchId)                      e.batchId               = "Batch is required";
    if (!formData.status.trim())                e.status                = "Status is required";
    return e;
  };

  const handleSliderConfirm = (ids, _removedIds, objects) => {
    setSelectedQuestions(objects);
  };

  const handleRemoveQuestion = (id) => {
    setSelectedQuestions(prev => prev.filter(q => q._id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const ve = validate();
    if (Object.keys(ve).length > 0) {
      setErrors(ve);
      setErrorMessage("Please correct the errors below.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        assignmentTime: `${formData.assignmentStartTime} - ${formData.assignmentEndTime}`,
        assignmentAnalysisTime: enableAnalysis
          ? `${formData.assignmentAnalysisStartTime} - ${formData.assignmentAnalysisEndTime}`
          : "",
        questionIds: selectedQuestions.map(q => q._id),
      };
      delete payload.assignmentStartTime;
      delete payload.assignmentEndTime;
      delete payload.assignmentAnalysisStartTime;
      delete payload.assignmentAnalysisEndTime;

      await createAssignment(payload);
      navigate("/assignments", { state: { successMessage: "Assignment created successfully!" } });
    } catch (err) {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="af-page">
      {loading && (
        <div className="af-overlay">
          <div className="af-spinner" />
          <p className="af-overlay-text">Creating assignment…</p>
        </div>
      )}

      <AssignmentQuestionPanel
        open={sliderOpen}
        onClose={() => setSliderOpen(false)}
        onConfirm={handleSliderConfirm}
        existingIds={selectedQuestions.map(q => q._id)}
        existingObjects={selectedQuestions}
      />

      <div className="af-card">
        <div className="af-header">
          <div className="af-header-icon amber">📝</div>
          <div className="af-header-text">
            <h2 className="af-title">Add New Assignment</h2>
            <p className="af-subtitle">Create and schedule an assignment for a batch</p>
          </div>
        </div>

        {successMessage && <div className="af-alert af-alert-success" style={{margin:"16px 32px 0"}}>{successMessage}</div>}
        {errorMessage   && <div className="af-alert af-alert-error"   style={{margin:"16px 32px 0"}}>{errorMessage}</div>}

        <form onSubmit={handleSubmit}>
          <div className="af-body">

            {/* Basic Info */}
            <div className="af-section">
              <p className="af-section-title">Assignment Information</p>
              <Field label="Title" required error={errors.assignmentTitle}>
                <input type="text" name="assignmentTitle" placeholder="e.g. React Hooks Exercise"
                  value={formData.assignmentTitle} onChange={handleChange}
                  className={errors.assignmentTitle ? "error-input" : ""} />
              </Field>
              <div className="af-row">
                <Field label="Duration" error={errors.assignmentDuration}>
                  <input type="text" name="assignmentDuration" placeholder="e.g. 2 hours"
                    value={formData.assignmentDuration} onChange={handleChange} />
                </Field>
                <Field label="Batch" required error={errors.batchId}>
                  <select name="batchId" value={formData.batchId} onChange={handleChange}
                    className={errors.batchId ? "error-input" : ""}>
                    <option value="">{batches.length === 0 ? "Loading…" : "Select batch"}</option>
                    {batches.map(b => <option key={b._id} value={b._id}>{formatBatchLabel(b)}</option>)}
                  </select>
                </Field>
              </div>
              <div className="af-field">
                <label className="af-label">Description<span className="af-required">*</span></label>
                <div className={`af-editor-wrap${errors.assignmentDescription ? " error-input" : ""}`}>
                  <RichTextEditor
                    value={formData.assignmentDescription}
                    onBlur={v => setFormData(p => ({ ...p, assignmentDescription: v }))}
                    toolbar="basic"
                    height={280}
                    placeholder="What should students do for this assignment…"
                  />
                </div>
                {errors.assignmentDescription && <span className="af-error">{errors.assignmentDescription}</span>}
              </div>
            </div>

            {/* Timing */}
            <div className="af-section">
              <p className="af-section-title">Assignment Window</p>
              <div className="af-field">
                <label className="af-label">Submission Window</label>
                <div className="af-time-row">
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Opens</label>
                    <input type="datetime-local" name="assignmentStartTime" value={formData.assignmentStartTime}
                      onChange={handleChange} className={errors.assignmentTime ? "error-input" : ""} />
                  </div>
                  <div className="af-field">
                    <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Closes</label>
                    <input type="datetime-local" name="assignmentEndTime" value={formData.assignmentEndTime}
                      onChange={handleChange} className={errors.assignmentTime ? "error-input" : ""} />
                  </div>
                </div>
                {errors.assignmentTime && <span className="af-error">{errors.assignmentTime}</span>}
              </div>

              <div className="af-field">
                <label className="aa-checkbox-label">
                  <input
                    type="checkbox"
                    className="aa-checkbox"
                    checked={enableAnalysis}
                    onChange={e => setEnableAnalysis(e.target.checked)}
                  />
                  Enable Analysis
                </label>
              </div>

              {enableAnalysis && (
                <div className="af-field">
                  <label className="af-label">Analysis Window <span style={{fontWeight:400,color:"#9ca3af",fontSize:"12px"}}>(optional)</span></label>
                  <div className="af-time-row">
                    <div className="af-field">
                      <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Opens</label>
                      <input type="datetime-local" name="assignmentAnalysisStartTime"
                        value={formData.assignmentAnalysisStartTime} onChange={handleChange} />
                    </div>
                    <div className="af-field">
                      <label className="af-label" style={{fontSize:"11px",color:"#9ca3af",textTransform:"uppercase"}}>Closes</label>
                      <input type="datetime-local" name="assignmentAnalysisEndTime"
                        value={formData.assignmentAnalysisEndTime} onChange={handleChange} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Attachment & Status */}
            <div className="af-section">
              <p className="af-section-title">Attachment & Status</p>
              <Field label="Attachment Link" error={errors.assignmentAttachment}>
                <input type="text" name="assignmentAttachment"
                  placeholder="Paste Google Drive / GitHub / Docs link… (optional)"
                  value={formData.assignmentAttachment}
                  onChange={e => { setFormData(p => ({...p, assignmentAttachment: e.target.value})); setErrors(p => ({...p, assignmentAttachment: ""})); }} />
              </Field>
              <Field label="Status" required error={errors.status}>
                <select name="status" value={formData.status} onChange={handleChange}
                  className={errors.status ? "error-input" : ""}>
                  <option value="">Select status…</option>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>

            {/* Questions */}
            <div className="af-section">
              <div className="aq-section-header">
                <p className="af-section-title" style={{margin:0}}>Questions <span style={{fontWeight:400,color:"#9ca3af",fontSize:"12px",textTransform:"none",letterSpacing:0}}>(optional)</span></p>
                <button type="button" className="aq-add-btn" onClick={() => setSliderOpen(true)}>
                  + Add Questions
                </button>
              </div>

              {selectedQuestions.length === 0 ? (
                <div className="aq-empty">No questions added yet. Click "Add Questions" to attach questions to this assignment.</div>
              ) : (
                <div className="aq-table-wrap">
                  <table className="aq-table">
                    <thead>
                      <tr>
                        <th className="aq-idx">#</th>
                        <th>Question</th>
                        <th>Type</th>
                        <th>Difficulty</th>
                        <th>Topic</th>
                        <th>Subject</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuestions.map((q, i) => (
                        <tr key={q._id}>
                          <td className="aq-idx">{i + 1}</td>
                          <td><div className="aq-q-text" title={q.question}>{q.question}</div></td>
                          <td>{q.questionType}</td>
                          <td>
                            <span className={`aq-badge ${DIFFICULTY_CLASS[q.difficultyLevel] || ""}`}>
                              {q.difficultyLevel}
                            </span>
                          </td>
                          <td>{q.topic}</td>
                          <td>{q.subject}</td>
                          <td>
                            <button type="button" className="aq-del-btn" onClick={() => handleRemoveQuestion(q._id)}>
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>

          <div className="af-submit-row">
            <button type="submit" className="af-submit" disabled={loading}>
              {loading ? "Creating…" : "Create Assignment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAssignments;
